import React, { useState, useEffect, useMemo } from 'react';
import { getTherapyMaterialByID, updateTherapyMaterial } from '../../../../controllers/TherapyMaterialController';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import ButtonPrimary from '../../../../components/button/ButtonPrimary';
import ButtonSecondary from '../../../../components/button/ButtonSecondary';
import Skeleton from '../../../../components/Skeleton';

const EditModal = ({ t }) => {
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [therapyMaterial, setTherapyMaterial] = useState(null);
    const [titleID, setTitleID] = useState('');
    const [titleEN, setTitleEN] = useState('');
    const [descriptionID, setDescriptionID] = useState('');
    const [descriptionEN, setDescriptionEN] = useState('');
    const [type, setType] = useState('');
    const [imageUrl, setImageUrl] = useState(null);
    let [articleUrl, setArticleUrl] = useState(null);
    const [videoUrl, setVideoUrl] = useState('');
    const [hours, setHours] = useState('');
    const [minutes, setMinutes] = useState('');
    const [seconds, setSeconds] = useState('');
    const navigate = useNavigate();
    const location = useLocation();
    const fromDetail = location.state?.fromDetail;
    
    useEffect(() => {
        const fetchTherapyMaterial = async () => {
            try {
                const result = await getTherapyMaterialByID(id);
                if (!result) {
                    throw new Error(t('therapyMaterial.fetchData.errorMessage'));
                };
                
                setTherapyMaterial(result);
                setTitleID(result.title_id);
                setTitleEN(result.title_en);
                setDescriptionID(result.description_id);
                setDescriptionEN(result.description_en);
                setType(result.type);
                setVideoUrl(result.video_url);
                const [h, m, s] = result.duration.split(':').map(Number);
                setHours(h || '0');
                setMinutes(m || '0');
                setSeconds(s || '0');
            } catch (error) {
                toast.error(error.message);
                navigate('/therapy-material');
            } finally {
                setLoading(false);
            };
        };
        
        fetchTherapyMaterial();
    }, [id, t, navigate]);
    
    const isDisabled = useMemo(() => {
        const isBasicFieldsValid =
            titleID.trim() &&
            titleEN.trim() &&
            descriptionID.trim() &&
            descriptionEN.trim() &&
            type.trim() &&
            (hours !== '' && hours !== null && hours !== undefined) &&
            (minutes !== '' && minutes !== null && minutes !== undefined) &&
            (seconds !== '' && seconds !== null && seconds !== undefined);
        const isFileRequired = type === 'Article' ? articleUrl || therapyMaterial?.article_url : type === 'Video' ? videoUrl : true;
        
        return !(isBasicFieldsValid && isFileRequired);
    }, [titleID, titleEN, descriptionID, descriptionEN, type, articleUrl, videoUrl, hours, minutes, seconds, therapyMaterial]);
    
    const handleUpdate = async () => {
        try {
            if (imageUrl && !imageUrl.type.startsWith('image/')) {
                toast.error(t('common.imageLink.errorMessageInvalidImageType'));
                return;
            };
            
            if (type === 'Article' && articleUrl && articleUrl.type !== 'application/pdf') {
                toast.error(t('common.articleLink.errorMessageInvalidArticleType'));
                return;
            };
            
            const formData = new FormData();
            formData.append('title_id', titleID);
            formData.append('title_en', titleEN);
            formData.append('description_id', descriptionID);
            formData.append('description_en', descriptionEN);
            formData.append('type', type);
            if (imageUrl) formData.append('image_url', imageUrl);
            if (articleUrl) formData.append('article_url', articleUrl);
            formData.append('video_url', videoUrl);
            
            const padTime = (value) => value.toString().padStart(2, '0');
            const formattedDuration = `${padTime(hours)}:${padTime(minutes)}:${padTime(seconds)}`;
            formData.append('duration', formattedDuration);
            
            await updateTherapyMaterial(id, formData);
            
            if (fromDetail) {
                navigate(`/therapy-material/${id}/detail`);
            } else {
                navigate('/therapy-material');
            };
            
            toast.success(t('therapyMaterial.edit.message'));
        } catch (error) {
            toast.error(error.message);
        };
    };
    
    const handleBack = () => {
        if (fromDetail) {
            navigate(`/therapy-material/${id}/detail`);
        } else {
            navigate('/therapy-material');
        };
    };
    
    const handleDurationChange = (e, field) => {
        let value = e.target.value.replace(/\D/g, '');
        if (field === 'hours') {
            value = Math.max(0, Math.min(23, parseInt(value || 0, 10)));
            setHours(value);
        } else if (field === 'minutes' || field === 'seconds') {
            value = Math.max(0, Math.min(59, parseInt(value || 0, 10)));
            if (field === 'minutes') setMinutes(value);
            else setSeconds(value);
        };
    };
    
    if (loading) {
        return (
            <Skeleton className='flex h-36 w-full rounded-lg' />
        ); 
    };
    
    return (
        <div className='flex flex-col p-5 rounded-lg bg-white space-y-5'>
            {/* Title */}
            <div className='flex flex-grow items-center'>
                <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.title.title')} ({t('languages.indonesian')}):</span>
                <input
                    className={`
                        w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                        hover:outline-none hover:border-grayX11
                        focus:outline-none focus:border-blackOlive
                    `}
                    type='text'
                    placeholder={t('common.title.placeholderText')}
                    value={titleID}
                    onChange={(e) => setTitleID(e.target.value)} 
                />
            </div>
            <div className='flex flex-grow items-center'>
                <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.title.title')} ({t('languages.english')}):</span>
                <input
                    className={`
                        w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                        hover:outline-none hover:border-grayX11
                        focus:outline-none focus:border-blackOlive
                    `}
                    type='text'
                    placeholder={t('common.title.placeholderText')}
                    value={titleEN}
                    onChange={(e) => setTitleEN(e.target.value)} 
                />
            </div>
            
            {/* Description */}
            <div className='flex flex-grow items-center'>
                <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.description.title')} ({t('languages.indonesian')}):</span>
                <input
                    className={`
                        w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                        hover:outline-none hover:border-grayX11
                        focus:outline-none focus:border-blackOlive
                    `}
                    type='text'
                    placeholder={t('common.description.placeholderText')}
                    value={descriptionID}
                    onChange={(e) => setDescriptionID(e.target.value)} 
                />
            </div>
            <div className='flex flex-grow items-center'>
                <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.description.title')} ({t('languages.english')}):</span>
                <input
                    className={`
                        w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                        hover:outline-none hover:border-grayX11
                        focus:outline-none focus:border-blackOlive
                    `}
                    type='text'
                    placeholder={t('common.description.placeholderText')}
                    value={descriptionEN}
                    onChange={(e) => setDescriptionEN(e.target.value)} 
                />
            </div>
            
            {/* Type */}
            <div className='flex flex-grow items-center'>
                <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.type.title')}:</span>
                <select
                    className={`
                        w-full px-3 py-2 rounded bg-white border border-platinum text-xs md:text-sm
                        hover:outline-none hover:border-grayX11
                        focus:outline-none focus:border-blackOlive
                    `}
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                >
                    <option value='' disabled={type !== ''}>{t('common.type.placeholderSelect')}</option>
                    <option value='Article'>{t('common.type.article')}</option>
                    <option value='Video'>{t('common.type.video')}</option>
                </select>
            </div>
            
            {/* Image */}
            <div className='flex flex-grow items-center'>
                <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.imageLink.title')}:</span>
                <label
                    htmlFor="image-input"
                    className="w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm cursor-pointer hover:outline-none hover:border-grayX11 focus:outline-none focus:border-blackOlive"
                >
                    {imageUrl ? imageUrl.name : t('common.imageLink.placeholderEdit')}
                    <input
                        type='file'
                        id='image-input'
                        accept='image/*'
                        className='hidden'
                        onChange={(e) => setImageUrl(e.target.files[0])}
                    />
                </label>
            </div>
            
            {/* Article */}
            {type === 'Article' && (
                <div className='flex flex-grow items-center'>
                    <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.articleLink.title')}:</span>
                    <label
                        htmlFor='article-input'
                        className='w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm cursor-pointer hover:outline-none hover:border-grayX11 focus:outline-none focus:border-blackOlive'
                    >
                        {articleUrl ? articleUrl.name : t('common.articleLink.placeholderEdit')}
                        <input
                            type='file'
                            id='article-input'
                            accept='.pdf'
                            className='hidden'
                            onChange={(e) => setArticleUrl(e.target.files[0])}
                        />
                    </label>
                </div>
            )}
            
            {/* Video */}
            {type === 'Video' && (
                <div className='flex flex-grow items-center'>
                    <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.videoLink.title')}:</span>
                    <input
                        className={`
                            w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        type='text'
                        placeholder={t('common.videoLink.placeholderText')}
                        value={videoUrl}
                        onChange={(e) => setVideoUrl(e.target.value)}
                    />
                </div>
            )}
            
            {/* Duration */}
            <div className='space-y-5'>
                <span className='text-base md:text-lg font-medium'>{t('common.duration.title')}:</span>
                <div className='flex flex-grow items-center'>
                    <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.duration.hours')}:</span>
                    <input
                        className={`
                            w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        type='text'
                        placeholder={t('common.duration.placeholderText')}
                        value={hours}
                        onChange={(e) => handleDurationChange(e, 'hours')}
                    />
                </div>
                <div className='flex flex-grow items-center'>
                    <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.duration.minutes')}:</span>
                    <input
                        className={`
                            w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        type='text'
                        placeholder={t('common.duration.placeholderText')}
                        value={minutes}
                        onChange={(e) => handleDurationChange(e, 'minutes')}
                    />
                </div>
                <div className='flex flex-grow items-center'>
                    <span className='w-36 lg:w-64 text-xs md:text-sm'>{t('common.duration.seconds')}:</span>
                    <input
                        className={`
                            w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        type='text'
                        placeholder={t('common.duration.placeholderText')}
                        value={seconds}
                        onChange={(e) => handleDurationChange(e, 'seconds')}
                    />
                </div>
            </div>
            <div className='flex justify-center sm:justify-end space-x-5'>
                <ButtonSecondary className='h-full w-24 md:w-32 md:text-sm' onClick={handleBack}>{t('common.action.back')}</ButtonSecondary>
                <ButtonPrimary className='h-full w-24 md:w-32 md:text-sm' isDisabled={isDisabled} onClick={handleUpdate}>{t('common.action.edit')}</ButtonPrimary>
            </div>
        </div>
    );
};

export default EditModal;